import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { PlayPauseButton } from '../../controls/PlayPauseButton';
import { PlayFromBeginningButton } from '../../controls/PlayFromBeginningButton';
import { SkipBackButton } from '../../controls/SkipBackButton';
import { SkipForwardButton } from '../../controls/SkipForwardButton';
import { PlaybackSpeedControl } from '../../controls/PlaybackSpeedControl';
import { AudioTimeIndicator } from '../../controls/AudioTimeIndicator';
import { getSharedAttributes } from '../../../utils/getSharedAttributes';
import { AudioFeature } from '../../../types/audioFeatureType';

const i18n = defineMessages({
	customizeButtonLabel: {
		id: 'audio.audio-popup.customize-button-label',
		defaultMessage: 'Customize',
		description: 'The label for the customize button in the audio popup',
	},
	sourcesButtonLabel: {
		id: 'audio.audio-popup.sources-button-label',
		defaultMessage: 'Sources',
		description: 'The label for the sources button in the audio popup',
	},
});

export type AudioBriefingPanelProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	handleFetchContent: () => void;
	onCustomizeClick: () => void;
	onSourcesClick: () => void;
	prefetchLabel?: string;
	loadingLabel?: string;
};

const styles = cssMap({
	wrapper: {
		flex: 1,
	},
	outerControlButtonWrapper: {
		// This is the width of the speed option that requires the most space
		// Ensures that the displayed speed is never truncated and that spacing remains uniform
		width: '54px',
	},
	controlsWrapper: {
		paddingBlock: token('space.100'),
	},
});

export const AudioBriefingPanel = ({
	audioRef,
	handleFetchContent,
	onCustomizeClick,
	onSourcesClick,
	prefetchLabel,
	loadingLabel,
}: AudioBriefingPanelProps) => {
	const [
		{ activeFeature, isPlaying, isLoading, hasFetched, playbackSpeed, lastGeneratedBriefingConfig },
	] = useAudioBriefing();

	const { formatMessage } = useIntl();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const isFeatureActive = activeFeature === AudioFeature.BRIEFING;
	const showExtraControls = isFeatureActive && hasFetched && !isLoading;

	const isFeatureLoading = isFeatureActive && isLoading;
	const isFeatureFetched = isFeatureActive && hasFetched;
	const isFeaturePlaying = isFeatureActive && isPlaying;

	const sharedAttributes = getSharedAttributes({
		customization: lastGeneratedBriefingConfig?.customization,
		additionalResources: lastGeneratedBriefingConfig?.additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	});

	const handlePlayFromBeginningButtonClick = () => {
		trackAIResultAction('audioPlayFromBeginning', {
			attributes: sharedAttributes,
		});
	};

	const handlePlayPauseButtonClick = () => {
		if (isPlaying) {
			trackAIResultAction('audioPause', {
				attributes: sharedAttributes,
			});
		} else {
			trackAIResultAction('audioPlay', {
				attributes: sharedAttributes,
			});
		}
	};

	const handleSkipBackButtonClick = () => {
		trackAIResultAction('audioSkipBackward', {
			attributes: sharedAttributes,
		});
	};

	const handleSkipForwardButtonClick = () => {
		trackAIResultAction('audioSkipForward', {
			attributes: sharedAttributes,
		});
	};

	const handlePlaybackSpeedButtonClick = (speed: number) => {
		trackAIResultAction('audioPlaybackSpeed', {
			attributes: { ...sharedAttributes, playbackSpeed: speed },
		});
	};

	return (
		<Box xcss={styles.wrapper} testId="audio-briefing-panel">
			<Stack space="space.150" alignInline="center">
				<Stack space="space.150" alignInline="center" xcss={styles.controlsWrapper}>
					<Inline space="space.150" alignBlock="center">
						{showExtraControls && (
							<>
								<Flex xcss={styles.outerControlButtonWrapper} justifyContent="center">
									<PlayFromBeginningButton
										audioRef={audioRef}
										onClick={handlePlayFromBeginningButtonClick}
										onRestart={handleFetchContent}
									/>
								</Flex>
								<SkipBackButton audioRef={audioRef} onClick={handleSkipBackButtonClick} />
							</>
						)}

						<PlayPauseButton
							audioRef={audioRef}
							isPlaying={isFeaturePlaying}
							isLoading={isFeatureLoading}
							hasFetched={isFeatureFetched}
							fetchContent={handleFetchContent}
							onClick={handlePlayPauseButtonClick}
						/>

						{showExtraControls && (
							<>
								<SkipForwardButton audioRef={audioRef} onClick={handleSkipForwardButtonClick} />
								<Box xcss={styles.outerControlButtonWrapper}>
									<PlaybackSpeedControl
										audioRef={audioRef}
										onClick={handlePlaybackSpeedButtonClick}
									/>
								</Box>
							</>
						)}
					</Inline>
					<AudioTimeIndicator
						audioRef={audioRef}
						isLoading={isFeatureLoading}
						hasFetched={isFeatureFetched}
						prefetchLabel={prefetchLabel}
						loadingLabel={loadingLabel}
					/>
				</Stack>
				<Inline space="space.200">
					<Tooltip content={formatMessage(i18n.sourcesButtonLabel)}>
						<IconButton
							appearance="subtle"
							icon={BookOpenIcon}
							onClick={onSourcesClick}
							label={formatMessage(i18n.sourcesButtonLabel)}
							testId="audio-briefing-sources-button"
						/>
					</Tooltip>
					<Tooltip content={formatMessage(i18n.customizeButtonLabel)}>
						<IconButton
							appearance="subtle"
							icon={CustomizeIcon}
							onClick={onCustomizeClick}
							label={formatMessage(i18n.customizeButtonLabel)}
							testId="audio-briefing-customize-button"
						/>
					</Tooltip>
				</Inline>
			</Stack>
		</Box>
	);
};
